/**
 * Central Pricing Constants (cents)
 *
 * Single source of truth for tier-based price values.
 * UI strings, plan IDs, and features live elsewhere (plans-data.ts, LandingPricing.tsx).
 */

export type PriceTier = "go" | "grow" | "glory"

/**
 * Month-2+ regular monthly price for NEW subscriptions (founder ruling 2026-10-24:
 * Go $79 / Grow $99 / Glory $129, Chargebee items plan-go-79 / plan-grow-99 /
 * plan-glory-129). Subscriptions created before PARITY_PRICING_CUTOFF keep the
 * legacy ladder below — existing subscribers' billing does not change.
 */
export const MONTHLY_PRICES_CENTS = { go: 7900, grow: 9900, glory: 12900 } as const
/**
 * Pre-parity regular monthly prices ($199/$299/$399). Still the renewal price of
 * every subscription created before PARITY_PRICING_CUTOFF, so code that serves
 * EXISTING subscribers (step-up cascade restore target, legacy charge jobs, MRR
 * estimates over the installed base) pins to this map, never MONTHLY_PRICES_CENTS.
 */
export const LEGACY_MONTHLY_PRICES_CENTS = { go: 19900, grow: 29900, glory: 39900 } as const
export const FIRST_MONTH_PRICES_CENTS = { go: 4900, grow: 6900, glory: 12900 } as const
/** Glory yearly is $720 billed upfront (plan-glory-720) since the 2026-10-24 parity ruling; Go/Grow unchanged. */
export const YEARLY_PRICES_CENTS = { go: 34800, grow: 49200, glory: 72000 } as const
export const YEARLY_MONTHLY_EQUIVALENT_CENTS = { go: 2900, grow: 4100, glory: 6000 } as const
/** Legacy Glory yearly ($1,188, plan-glory-1188) — still the renewal amount of pre-parity Glory yearly subs. */
export const LEGACY_GLORY_YEARLY_PRICE_CENTS = 118800
export const LEGACY_GLORY_YEARLY_MONTHLY_EQUIVALENT_CENTS = 9900

/**
 * Yearly plan IDs sold at checkout (billing-period detection + checkout allowlist).
 * plan-glory-1188 stays so a stale tab / old migration link still resolves; the
 * current Glory yearly id is plan-glory-720.
 */
export const YEARLY_PLAN_IDS = new Set(["plan-go-348", "plan-grow-492", "plan-glory-720", "plan-glory-1188"])

/**
 * Yearly plan id → its upfront price in cents. A yearly checkout is charged this
 * amount at signup (no trial since 2026-10-24). Map-asserted, never parsed from the id.
 */
export const YEARLY_PLAN_PRICES: Record<string, number> = {
    "plan-go-348": 34800,
    "plan-grow-492": 49200,
    "plan-glory-720": 72000,
    "plan-glory-1188": LEGACY_GLORY_YEARLY_PRICE_CENTS,
}

/**
 * Full set of yearly plan ids (current + every legacy yearly id that can still be the
 * live `item_price_id` on an un-migrated subscription). Used by the cancel-save flow to
 * BLOCK price-mutating retention offers (downgrade/discount) on yearly subs, which would
 * otherwise apply a MONTHLY downsell cents value to the ANNUAL item price.
 *
 * Source: legacy_to_new_pricing_migration.ts — every legacy id whose `new` is one of
 * plan-go-348 / plan-grow-492 / plan-glory-1188.
 */
export const YEARLY_PLAN_ID_SET = new Set<string>([
    // Current yearly plan ids
    "plan-go-348",
    "plan-grow-492",
    "plan-glory-720",
    // Pre-parity Glory yearly (live subscriptions renew on it)
    "plan-glory-1188",
    // Legacy yearly ids that map to plan-go-348
    "plan-basic-700",
    "plan-basic-1800",
    "plan-go-1800",
    "plan-350",
    // Legacy yearly ids that map to plan-grow-492
    "plan-pro-1180",
    "plan-pro-2150",
    "plan-grow-2150",
    "plan-699",
    // Legacy yearly ids that map to plan-glory-1188
    "plan-turbo-2140",
    "plan-turbo-3000",
    "plan-glory-3000",
    "plan-1799",
])

/** True when a plan id is any (current or legacy) yearly plan. */
export function isYearlyPlanId(planId: string | null | undefined): boolean {
    return !!planId && YEARLY_PLAN_ID_SET.has(planId)
}

/** Map monthly plan ID → yearly equivalent plan ID (used by switch-to-yearly upsell) */
export const MONTHLY_TO_YEARLY_PLAN: Record<string, string> = {
    // Pre-parity monthly plans (Go/Grow yearly prices unchanged; Glory yearly is now $720)
    "plan-go-199": "plan-go-348",
    "plan-grow-299": "plan-grow-492",
    "plan-glory-399": "plan-glory-720",
    // Parity monthly plans (2026-10-24)
    "plan-go-79": "plan-go-348",
    "plan-grow-99": "plan-grow-492",
    "plan-glory-129": "plan-glory-720",
}

/** Map yearly total (cents) → monthly equivalent (cents) for MRR calculations */
export const YEARLY_AMOUNT_TO_MONTHLY: Record<number, number> = {
    [YEARLY_PRICES_CENTS.go]: YEARLY_MONTHLY_EQUIVALENT_CENTS.go,
    [YEARLY_PRICES_CENTS.grow]: YEARLY_MONTHLY_EQUIVALENT_CENTS.grow,
    [YEARLY_PRICES_CENTS.glory]: YEARLY_MONTHLY_EQUIVALENT_CENTS.glory,
    [LEGACY_GLORY_YEARLY_PRICE_CENTS]: LEGACY_GLORY_YEARLY_MONTHLY_EQUIVALENT_CENTS,
}

// ---------------------------------------------------------------------------
// Plan-ID-based pricing maps (used by process_nmi_renewal.ts)
// ---------------------------------------------------------------------------

/**
 * Maps first-month/trial plan IDs to their PRE-PARITY regular counterparts.
 * If the migration cron (change_first_month_subscriptions) misses a subscription,
 * the safety net in process_nmi_renewal catches it at renewal time.
 *
 * COHORT SPLIT (2026-10-24): this static map is the regular plan of subscriptions
 * created BEFORE PARITY_PRICING_CUTOFF. Anything that promotes a `-first`
 * subscription to its regular plan MUST go through regularPlanForFirstMonthPlan()
 * with the subscription's created_at — a sub created at/after the cutoff swaps to
 * the parity plan (plan-go-79 / plan-grow-99 / plan-glory-129). The keys also serve
 * isFirstMonthPlanId() and the checkout allowlist.
 */
export const FIRST_MONTH_TO_REGULAR_PLAN: Record<string, string> = {
    // Legacy pricing plans
    "plan-first-month-69": "plan-go-199",
    "plan-1-day-trial-69": "plan-go-199",
    "plan-first-month-99": "plan-grow-299",
    "plan-1-day-trial-99": "plan-grow-299",
    "plan-first-month-199": "plan-glory-399",
    "plan-1-day-trial-199": "plan-glory-399",
    // Legacy pricing with old names
    "plan-basic-149-first": "plan-go-199",
    "plan-basic-69-first": "plan-go-199",
    "plan-pro-199-first": "plan-grow-299",
    "plan-pro-99-first": "plan-grow-299",
    "plan-turbo-299-first": "plan-glory-399",
    "plan-turbo-129-first": "plan-glory-399",
    // Current pricing plans (Go/Grow/Glory)
    "plan-go-49-first": "plan-go-199",
    "plan-go-69-first": "plan-go-199",
    "plan-grow-69-first": "plan-grow-299",
    "plan-grow-99-first": "plan-grow-299",
    "plan-glory-129-first": "plan-glory-399",
}

/**
 * True when the plan id is a FIRST-MONTH promo id (any generation, incl. legacy).
 * Used by the dunning cron to keep never-converted CC-trial rows OFF every
 * repricing path (aggressive Day-3 downsold retry, scheduled downsells, the
 * extended-retry reprice-to-lowest): repricing a first-month sub/invoice desyncs
 * from process_nmi_renewal STEP 2.8's bidirectional first-month clamp, which
 * would re-clamp the charged amount away from the invoice total (display↔charge
 * parity break). First-month rows must only ever retry the existing invoice at
 * its own total.
 */
export function isFirstMonthPlanId(planId: string | null | undefined): boolean {
    return !!planId && planId in FIRST_MONTH_TO_REGULAR_PLAN
}

/**
 * Maps first-month plan IDs to their correct price in cents.
 * Used by CC trial pricing enforcement to override incorrect invoice amounts.
 */
export const FIRST_MONTH_PLAN_PRICES: Record<string, number> = {
    "plan-go-49-first": 4900,
    "plan-go-69-first": 6900,
    "plan-grow-69-first": 6900,
    "plan-grow-99-first": 9900,
    "plan-glory-129-first": 12900,
}

/**
 * Maps regular plan IDs to their first-month price in cents.
 * Used when a CC trial subscription has already been migrated to the regular plan
 * before the first charge — we still enforce first-month pricing.
 */
export const REGULAR_PLAN_TO_FIRST_MONTH: Record<string, number> = {
    "plan-go-199": 4900,
    "plan-grow-299": 6900,
    "plan-glory-399": 12900,
    "plan-go-79": 4900,
    "plan-grow-99": 6900,
    "plan-glory-129": 12900,
}

/**
 * Maps regular plan IDs to their regular monthly price in cents.
 * Used in first-month plan safety net alerting.
 */
export const REGULAR_PLAN_PRICES: Record<string, number> = {
    "plan-go-199": 19900,
    "plan-grow-299": 29900,
    "plan-glory-399": 39900,
    "plan-go-79": 7900,
    "plan-grow-99": 9900,
    "plan-glory-129": 12900,
}

// ---------------------------------------------------------------------------
// Parity pricing cohort cutoff (founder ruling 2026-10-24)
// ---------------------------------------------------------------------------

/**
 * Subscriptions created AT OR AFTER this instant are on parity pricing: their
 * `-first` plan swaps to plan-go-79 / plan-grow-99 / plan-glory-129 at month 2.
 * Subscriptions created BEFORE it keep the pre-parity ladder ($199/$299/$399).
 *
 * MUST be set to the production deploy moment (or slightly EARLIER) at ship
 * time: a cutoff later than the deploy would swap post-deploy signups to the old
 * $199-399 plans.
 */
export const PARITY_PRICING_CUTOFF = "2026-10-24T16:00:00Z"
export const PARITY_PRICING_CUTOFF_MS = Date.parse(PARITY_PRICING_CUTOFF)

/** Pre-parity regular plan id → its parity replacement (same tier). */
export const LEGACY_TO_PARITY_REGULAR_PLAN: Record<string, string> = {
    "plan-go-199": "plan-go-79",
    "plan-grow-299": "plan-grow-99",
    "plan-glory-399": "plan-glory-129",
}

/** Regular monthly plan id per tier, by cohort. */
export const LEGACY_MONTHLY_PLAN_BY_TIER: Record<PriceTier, string> = {
    go: "plan-go-199",
    grow: "plan-grow-299",
    glory: "plan-glory-399",
}
export const PARITY_MONTHLY_PLAN_BY_TIER: Record<PriceTier, string> = {
    go: "plan-go-79",
    grow: "plan-grow-99",
    glory: "plan-glory-129",
}

/**
 * Normalize a subscription created_at to epoch ms. Chargebee returns unix
 * SECONDS; callers may also pass ms or an ISO string. Returns null when unknown.
 */
export function subscriptionCreatedAtMs(createdAt: number | string | null | undefined): number | null {
    if (typeof createdAt === "number" && Number.isFinite(createdAt) && createdAt > 0) {
        // < 1e12 → seconds (1e12 ms = 2001-09-09); anything larger is already ms.
        return createdAt < 1e12 ? createdAt * 1000 : createdAt
    }
    if (typeof createdAt === "string" && createdAt.length > 0) {
        const ms = Date.parse(createdAt)
        return Number.isFinite(ms) ? ms : null
    }
    return null
}

/** True when a subscription created at `createdAtMs` is on parity pricing; null when unknown. */
export function isParityPricingCohort(createdAtMs: number | null | undefined): boolean | null {
    if (typeof createdAtMs !== "number" || !Number.isFinite(createdAtMs) || createdAtMs <= 0) return null
    return createdAtMs >= PARITY_PRICING_CUTOFF_MS
}

/**
 * The regular (month-2+) plan a `-first` subscription must swap to, split by cohort:
 *   created before PARITY_PRICING_CUTOFF → FIRST_MONTH_TO_REGULAR_PLAN (plan-go-199 …)
 *   created at/after the cutoff         → its parity plan (plan-go-79 …)
 *
 * Returns null when `firstMonthPlanId` is not a first-month plan, OR when the
 * creation time is unknown — callers must then NOT promote (never guess a cohort:
 * guessing old overcharges a new subscriber, guessing new undercharges an old one).
 */
export function regularPlanForFirstMonthPlan(
    firstMonthPlanId: string | null | undefined,
    subscriptionCreatedAt: number | string | null | undefined
): string | null {
    if (!firstMonthPlanId || !Object.hasOwn(FIRST_MONTH_TO_REGULAR_PLAN, firstMonthPlanId)) return null
    const legacyRegular = FIRST_MONTH_TO_REGULAR_PLAN[firstMonthPlanId]
    const parity = isParityPricingCohort(subscriptionCreatedAtMs(subscriptionCreatedAt))
    if (parity === null) return null
    if (!parity) return legacyRegular
    return LEGACY_TO_PARITY_REGULAR_PLAN[legacyRegular] ?? legacyRegular
}

/** Legacy tier aliases → current tier names (for create_chargebee_payment.ts compatibility) */
export const LEGACY_TIER_TO_CURRENT: Record<string, PriceTier> = {
    basic: "go", pro: "grow", turbo: "glory",
    go: "go", grow: "grow", glory: "glory",
}
